import React, { createContext, useReducer, useContext, useEffect, useRef } from 'react';

const NominationFormContext = createContext();
const CURRENT_DATA_VERSION = '1.0';
const LOCAL_STORAGE_KEY = 'nominationFormData';

// التحقق من توفر localStorage مرة واحدة فقط لتجنب الاستدعاءات المتكررة
let isStorageAvailable = null;

const isLocalStorageAvailable = () => {
  // إذا كنا قد تحققنا من قبل، استخدم القيمة المخزنة
  if (isStorageAvailable !== null) {
    return isStorageAvailable;
  }
  
  try {
    const testKey = '__test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    isStorageAvailable = true;
  
    return true;
  } catch (e) {
    isStorageAvailable = false;
    return false;
  }
};

const getInitialState = () => {
  if (isLocalStorageAvailable()) {
    try {
      const savedData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData.version === CURRENT_DATA_VERSION) {
          return {
            ...parsedData,
            hasChanges: false // بدء التطبيق بدون تغييرات
          };
        }
      }
    } catch (error) {
      console.error("Failed to load or parse saved form data.", error);
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }
  // الحالة الأولية الافتراضية
  return {
    version: CURRENT_DATA_VERSION,
    currentStep: 0,
    nominationId: null,
    formData: {
      personalInfo: {},
      documents: {},
      nomination: {},
    },
    formProgress: {
      step0: true,
      step1: false,
      step2: false,
      step3: false,
    },
    lastSaved: null,
    hasChanges: false, // إضافة حالة التغييرات
  };
};
// حفظ البيانات تلقائياً في التخزين المحلي
const saveToLocalStorage = (state) => {
  if (isLocalStorageAvailable()) {
    try {
      const dataToSave = {
        ...state,
        lastSaved: new Date().toISOString(),
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Failed to auto-save form data to localStorage:', error);
    }
  }
};

const formReducer = (state, action) => {
  let newState;
  
  switch (action.type) {
    case 'UPDATE_STEP_DATA': {
      const { step, data, isValid } = action.payload;

      // قم بإنشاء نسخ جديدة من أجزاء الحالة التي ستتغير
      const newFormData = { ...state.formData };
      const newFormProgress = { ...state.formProgress };

      // حدد الجزء الذي سيتغير بناءً على رقم الخطوة
      switch (step) {
        case 1:
          newFormData.personalInfo = data;
          newFormProgress.step1 = isValid;
          break;
        case 2:
          newFormData.documents = data;
          newFormProgress.step2 = isValid;
          break;
        case 3:
          newFormData.nomination = data;
          newFormProgress.step3 = isValid;
          break;
        default:
          return state;
      }

      // إنشاء الحالة الجديدة
      newState = {
        ...state,
        formData: newFormData,
        formProgress: newFormProgress,
        hasChanges: true, // تعليم أن هناك تغييرات جديدة
      };
      
      // لا نقوم بالحفظ التلقائي هنا، سيتم ذلك بواسطة المؤقت كل 20 ثانية
      
      return newState;
    }

    // تم إزالة حالة SET_NOMINATION_ID لأننا لم نعد بحاجة إليها

    case 'SET_CURRENT_STEP':
      newState = { ...state, currentStep: action.payload, hasChanges: true };
      return newState;
      
    case 'UPDATE_FORM_DATA':
      newState = { ...state, formData: { ...state.formData, ...action.payload }, hasChanges: true };
      return newState;
      
    case 'UPDATE_FORM_PROGRESS':
      newState = { ...state, formProgress: { ...state.formProgress, ...action.payload }, hasChanges: true };
      return newState;
      
    case 'SET_NOMINATION_ID':
      newState = { ...state, nominationId: action.payload, hasChanges: true };
      return newState;
      
    case 'SAVE_FORM_TO_STORAGE':
      saveToLocalStorage(state);
      return { ...state, hasChanges: false }; // إعادة تعيين حالة التغييرات بعد الحفظ
      
    case 'RESET_FORM':
      if (isLocalStorageAvailable()) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
      return getInitialState();
      
    case 'CLEAR_DRAFT':
      if (isLocalStorageAvailable()) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
      return state;
      
    default:
      return state;
  }
};

export const NominationFormProvider = ({ children }) => {
  const [state, dispatch] = useReducer(formReducer, getInitialState());
  const autoSaveTimerRef = useRef(null);

  // حفظ تلقائي كل 20 ثانية فقط إذا كانت هناك تغييرات
  useEffect(() => {
    // تنظيف المؤقت السابق إذا وجد
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
    }
    
    // إنشاء مؤقت جديد لحفظ البيانات كل 20 ثانية
    autoSaveTimerRef.current = setInterval(() => {
      // حفظ البيانات فقط إذا كانت هناك تغييرات
      if (state.hasChanges) {
        saveToLocalStorage(state);
        // تحديث حالة التغييرات بعد الحفظ
        dispatch({ type: 'SAVE_FORM_TO_STORAGE' });
      }
    }, 20000); // 20 ثانية
    
    // تنظيف المؤقت عند إزالة المكون
    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [state, dispatch]);

  const value = { state, dispatch };

  return (
    <NominationFormContext.Provider value={value}>
      {children}
    </NominationFormContext.Provider>
  );
};

export const useNominationForm = () => {
  const context = useContext(NominationFormContext);
  if (context === undefined) {
    throw new Error('useNominationForm must be used within a NominationFormProvider');
  }
  return context;
};
